import { useCallback, useRef, useState } from 'react'

const useVoiceRecorder = () => {
  const [isRecording, setIsRecording] = useState(false)
  const [isVoiceRecordingSupported, setIsVoiceRecordingSupported] =
    useState(true)
  const [audio, setAudio] = useState<Blob | null>(null)
  const recorder = useRef<MediaRecorder>()
  const chunks = useRef<BlobPart[]>([])

  const startRecording = useCallback(async () => {
    if (navigator.mediaDevices) {
      chunks.current = []
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        })
        if (stream) {
          setIsVoiceRecordingSupported(true)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(window as any).localStream = stream
          recorder.current = new MediaRecorder(stream)
          recorder.current.ondataavailable = (e) => {
            chunks.current.push(e.data)
            if (recorder.current && recorder.current.state === 'inactive') {
              const blob = new Blob(chunks.current, { type: 'audio/webm' })
              setAudio(blob)
            }
          }
          recorder.current.onstop = () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ;(window as any).localStream.getTracks()[0].stop()
            setIsRecording(false)
          }
          recorder.current.start()
          setIsRecording(true)
          return
        }
      } catch {
        // nop catch
        // setting state will be done at the end of the function
      }
    }
    setIsVoiceRecordingSupported(false)
    setIsRecording(false)
  }, [])

  const stopRecording = useCallback(() => {
    if (recorder.current?.state === 'recording') {
      recorder.current.stop()
    }
  }, [])

  const toggleRecording = useCallback(() => {
    isRecording ? stopRecording() : startRecording()
  }, [isRecording, startRecording, stopRecording])

  return {
    audio,
    isRecording,
    isVoiceRecordingSupported,
    startRecording,
    stopRecording,
    toggleRecording,
  }
}

export default useVoiceRecorder
